import React, { useEffect, useState } from 'react';
import './jobStatusTableDetails.scss';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { fetchJobDetailsInfo } from 'actions/jobDetail';
import { useLocation, useParams } from 'react-router-dom';
import { Table } from '@tfecom-thermofisher/react-komodo-design';
import Loading from 'components/Placeholders/Loading';
import { JOB_STATUS_LABEL_MAPPING, JOB_STATUS_MAPPING, REFRESH_BUTTON_TITLE } from 'constants/constants';
import classNames from 'classnames';
import { CorePagination, CoreSearch } from '@komodo-design-system/komodo-components-react';
import { buildBreadCrumbData } from 'utils/utils';
import BreadCrumbsComponent from 'componentsV2/Breadcrumbs/BreadCrumbsComponent';

const JobStatusTableDetails = () => {
  const location = useLocation();
  const params = useParams();
  const [pageNumber, setPageNumber] = useState(0);
  const [jobDetailData, setJobDetailData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortFilter, setSortFilter] = useState({
    sortBy: {
      id: '',
      dir: '',
      isSort: false
    },
    filters: {}
  });
  const [searchInput, setSearchInput] = useState('');
  const [minText, setMinText] = useState(true);
  const [filteredResults, setFilteredResults] = useState([]);

  const onConfigChange = async (dat) => {
    setIsLoading(true);
    if (dat.sortBy.id !== '') {
      const dir = dat.sortBy.dir;      
      const response = await fetchJobDetailsInfo.fetchJobDetails(params.id, 0, dir?.toUpperCase(), dat.sortBy.id);
      setIsLoading(false);
      if (response) {
        setJobDetailData(response);
        setFilteredResults(response); //response
      }
      setPageNumber(0);
      setSortFilter({
        sortBy: dat.sortBy,
        filters: dat.filters
      });
    }
    else {
      const response = await fetchJobDetailsInfo.fetchJobDetails(params.id, 0, 'DESC', 'assetName');
      setIsLoading(false);
      if (response) {
        setJobDetailData(response);
        setFilteredResults(response); //response
      }
      setPageNumber(0);
      setSortFilter({
        sortBy: dat?.sortBy,
        filters: dat?.filters
      });
    }
  };

  const searchItems = (searchValue) => {
    setSearchInput(searchValue);
    if (!searchValue) {
      setFilteredResults(jobDetailData);
      setMinText(true);
    } else if (searchValue.length > 2) {
      const filteredRows = jobDetailData?.list?.filter((key) => {
        return (
          key?.assetId?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.assetName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.status?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.assetJobId?.toString().toLowerCase().includes(searchValue.toLowerCase())
        );
      });      
      setFilteredResults({
        list: filteredRows,
        totalCount: filteredRows.length
      });
      setMinText(false);
    }
  };

  useEffect(() => {
    const JobStatusTableData = async () => {
      setIsLoading(true);
      const response = await fetchJobDetailsInfo.fetchJobDetails(params.id, 0, 'DESC', 'assetName');
      setIsLoading(false);
      setJobDetailData(response);
      setFilteredResults(response); //response
    };
    if (!jobDetailData) {
      JobStatusTableData();
    }
  }, [jobDetailData, params.id]);

  const cellDataSorting = (tableData) => {
    const column = tableData?.column;
    const defaultSortable = {
      active: false,
      options: []
    };

    const sortable =
      column.sortable === true
        ? {
          ...defaultSortable,
          active: true
        }
        : { ...defaultSortable, ...column.sortable };
    return sortable;
  };

  const columns = [
    {
      Header: 'Job ID',
      accessor: 'assetJobId',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {tableCellData.assetJobId}
          </span>
        );
      }
    },
    {
      Header: 'Asset ID',
      accessor: 'assetId',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {tableCellData?.assetId?.length > 40
              ? `${tableCellData?.assetId?.substring(0, 40)}...`
              : tableCellData?.assetId}
          </span>
        );
      }
    },
    {
      Header: 'Asset Name',
      accessor: 'assetName',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {tableCellData.assetName}
          </span>
        );
      }
    },
    {
      Header: 'Start Date',
      accessor: 'createdDate',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {tableCellData?.createdDate
              ? dayjs(tableCellData?.createdDate.split('T')[0]).format(
                'MM-DD-YYYY'
              )
              : '-'}
          </span>
        );
      }
    },
    {
      Header: 'Asset Status',
      accessor: 'status',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const cellData = tableDetailedData?.data[index];
        const isStatusLabel = JOB_STATUS_LABEL_MAPPING[cellData.status?.toLowerCase()];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            <span className={`status-label ${isStatusLabel}`}>
              {JOB_STATUS_MAPPING[cellData.status?.toLowerCase()]}
            </span>
          </span>
        );
      }
    },
    {
      Header: 'Completed Date',
      accessor: 'updatedDate',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        const isInProgress =
          tableCellData?.status.toLowerCase() === 'created' ||
          tableCellData?.status.toLowerCase() === 'progress';
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {!isInProgress ? (
              <span>
                {dayjs(tableCellData?.updatedDate?.split('T')[0]).format(
                  'MM-DD-YYYY'
                )}
              </span>
            ) : (
              '-'
            )}
          </span>
        );
      }
    },
    {
      Header: 'Failure Message',
      accessor: 'errorMessage',
      sortable: true,
      Cell: (tableDetailedData) => {
        const sortable = cellDataSorting(tableDetailedData);
        const index = tableDetailedData?.row?.index;
        const tableCellData = tableDetailedData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {tableCellData.errorMessage}
          </span>
        );
      }
    }
  ];

  const handlePagination = async (event) => {
    const pgNumber = event.detail.activePage;
    if (pgNumber - 1 === pageNumber) {
      return;
    }
    setPageNumber(pgNumber - 1);
    setIsLoading(true);
    const response = await fetchJobDetailsInfo.fetchJobDetails(
      params.id,
      pgNumber - 1,
      sortFilter.sortBy.dir?.toUpperCase(),
      sortFilter.sortBy.id
    );
    setIsLoading(false);
    if (response) {
      setJobDetailData(response);
      setFilteredResults(response);
    }
  };

  const onRefreshClick = async () => {
    setIsLoading(true);
    const response = await fetchJobDetailsInfo.fetchJobDetails(params.id, 0, "DESC", 'assetName');
    setIsLoading(false);
    if (response) {
      setJobDetailData(response);
      setFilteredResults(response);
      setSearchInput('');
    }
  };

  return (
    <>
      <BreadCrumbsComponent
        breadCrumbData={buildBreadCrumbData(location.pathname)}
      />
      <section className="lty-jobCompomentBlock jobDetailed-tablecomponent">
        <div className="table-title">
          <h2>
            <FormattedMessage id="detailedView" />
          </h2>
          {jobDetailData?.list?.length > 0 && (
            <span
              className="icon-24-refresh-bold"
              onClick={onRefreshClick}
              title={REFRESH_BUTTON_TITLE}></span>
          )}
        </div>
        <div className="search_block">
          <CoreSearch
            id='coreSearch'
            style={{ display: 'inline-block', width: '300px' }}
            placeholder="Search"
            value={searchInput}
            onSearchCleared={() => searchItems('')}
            onSearchQueryChanged={(e) => searchItems(e.detail)}
          />
          {minText && (
            <span className="indication_text">
              Please enter alteast 3 characters.
            </span>
          )}
          {filteredResults?.list?.length === 0 && searchInput.length > 0 && (
            <span className="errorMsg">Sorry, no matching record found</span>
          )}
        </div>
        {filteredResults?.list?.length > 0 ? (
          <Table
            columns={columns}
            data={filteredResults?.list || []}
            onConfigChange={onConfigChange}
            size="normal"
            sortFilter={sortFilter}
          />
        ) : (
          <div className="noData">
            <p className="blk-jobstatus-tablecomponent__nodata">
              <FormattedMessage id="noDatatoShow" />
            </p>
          </div>
        )}
        {filteredResults?.totalCount > 10 ? (
          <CorePagination
            totalresults={filteredResults?.totalCount}
            variants='{"dropdown": false, "indicator": false, "goto": false}'
            resultsperpage={[10]}
            onActivePageChange={handlePagination}
            activepage={pageNumber + 1}
          ></CorePagination>
        ) : null}
        {isLoading && <Loading />}
      </section>
    </>
  );
};

export default JobStatusTableDetails;
